'use client';

import React, { useMemo } from 'react';
import { cx } from '../utils/cx';

export type DividerOrientation = 'horizontal' | 'vertical';

export interface DividerProps {
  orientation?: DividerOrientation;
  thickness?: number;
  color?: string;
  indent?: number;
  className?: string;
}

function isCssColorValue(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return (
    v.startsWith('#') ||
    v.startsWith('rgb(') ||
    v.startsWith('rgba(') ||
    v.startsWith('hsl(') ||
    v.startsWith('hsla(') ||
    v.startsWith('var(')
  );
}

function resolveColorToken(token: string | undefined): string {
  const t = String(token ?? 'divider').trim();
  if (!t) return 'currentColor';
  if (isCssColorValue(t)) return t;
  return `var(--color-${t}, currentColor)`;
}

export function Divider({
  orientation = 'horizontal',
  thickness = 1,
  color = 'divider',
  indent = 0,
  className,
}: DividerProps) {
  const safeThickness = Number.isFinite(thickness) ? Math.max(1, Math.round(thickness)) : 1;
  const safeIndent = Number.isFinite(indent) ? Math.max(0, Math.round(indent)) : 0;

  const resolvedColor = useMemo(() => resolveColorToken(color), [color]);

  if (orientation === 'vertical') {
    return (
      <div
        role="separator"
        aria-orientation="vertical"
        aria-hidden="true"
        className={cx('shrink-0', className)}
        style={{
          width: `${safeThickness}px`,
          alignSelf: 'stretch',
          marginLeft: safeIndent ? `${safeIndent}px` : undefined,
          backgroundColor: resolvedColor,
          opacity: 0.35,
        }}
      />
    );
  }

  return (
    <hr
      aria-hidden="true"
      className={cx('border-0', className)}
      style={{
        height: `${safeThickness}px`,
        marginLeft: safeIndent ? `${safeIndent}px` : undefined,
        backgroundColor: resolvedColor,
        opacity: 0.35,
      }}
    />
  );
}

Divider.displayName = 'Divider';
